import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";

/** Page-specific values contributed to the agent chatbot, such as `claim_id` or `member_id`. */
export type AgentChatPageContext = Record<string, unknown>;

/**
 * Reserved context key a page sets to `true` to opt into sending a text snapshot of its rendered
 * DOM. It is stripped before the context is sent, so it never reaches the agent as a value.
 */
export const PAGE_SNAPSHOT_OPT_IN_KEY = "__isPageSnapshotEnabled";

/**
 * Reserved context key a single page sets to `true` to turn the chatbot on for itself while it is
 * mounted, without its module opting in through `metadata.json`. It is stripped before the context
 * is sent, so it never reaches the agent as a value.
 */
export const PAGE_AGENT_CHAT_OPT_IN_KEY = "__isAgentChatPageEnabled";

/** The agent chat context registry model. */
interface AgentChatContextValue {
  /** The merged context published by every currently mounted contributor. */
  readonly pageContext: AgentChatPageContext;

  /** Publishes (or replaces) the context contributed by a single contributor. */
  readonly publish: (contributorId: string, context: AgentChatPageContext) => void;

  /** Removes the context contributed by a single contributor. */
  readonly unpublish: (contributorId: string) => void;
}

const EmptyContext: AgentChatPageContext = {};

const AgentChatContext = createContext<AgentChatContextValue>({
  pageContext: EmptyContext,
  publish: () => undefined,
  unpublish: () => undefined,
});

/** The `AgentChatContextProvider` component properties. */
export interface AgentChatContextProviderProps {
  /** The component children. */
  readonly children?: ReactNode;
}

/**
 * Holds the page context contributed by mounted pages so the shell-level agent chatbot can attach
 * it to outgoing messages. Contributions are keyed by contributor so unmounting a page removes
 * only its own values.
 */
export const AgentChatContextProvider: FC<AgentChatContextProviderProps> = ({ children }) => {
  const [contributions, setContributions] = useState<Record<string, AgentChatPageContext>>({});

  const pageContext = useMemo(() => {
    const merged: AgentChatPageContext = {};
    for (const contribution of Object.values(contributions)) {
      Object.assign(merged, contribution);
    }
    return merged;
  }, [contributions]);

  const publish = useCallback((contributorId: string, context: AgentChatPageContext) => {
    setContributions((previous) => ({ ...previous, [contributorId]: context }));
  }, []);

  const unpublish = useCallback((contributorId: string) => {
    setContributions((previous) => {
      if (!(contributorId in previous)) return previous;

      const next = { ...previous };
      delete next[contributorId];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ pageContext, publish, unpublish }),
    [pageContext, publish, unpublish],
  );

  return <AgentChatContext.Provider value={value}>{children}</AgentChatContext.Provider>;
};

/**
 * Publishes page-specific context to the shell agent chatbot for as long as the calling component
 * is mounted. Call it from any page that knows identifiers worth giving the agent.
 *
 * @example
 * useAgentChatContext({ claim_id: claimId });
 *
 * @example
 * // opt into sending a text snapshot of the rendered page
 * useAgentChatContext({ claim_id: claimId, [PAGE_SNAPSHOT_OPT_IN_KEY]: true });
 *
 * @example
 * // turn the chatbot on for this page alone, in a module that has not opted in
 * useAgentChatContext({ [PAGE_AGENT_CHAT_OPT_IN_KEY]: true });
 */
export const useAgentChatContext = (context: AgentChatPageContext): void => {
  const contributorId = useId();
  const { publish, unpublish } = useContext(AgentChatContext);

  /* Serializing keeps the effect from re-running for object identity changes alone, which is the
     common case when callers build the context inline on every render. A page could publish a value
     that cannot be serialized (a circular object, a BigInt), and crashing the whole page over it
     would be a poor trade, so fall back to the key names: still stable across renders, so the
     effect keeps publishing once rather than looping on every render. */
  let serializedContext: string;
  try {
    serializedContext = JSON.stringify(context ?? {}) ?? "";
  } catch {
    serializedContext = Object.keys(context ?? {}).join(",");
  }

  const contextRef = useRef(context);
  contextRef.current = context;

  useEffect(() => {
    publish(contributorId, contextRef.current ?? {});
  }, [contributorId, publish, serializedContext]);

  useEffect(() => {
    return () => unpublish(contributorId);
  }, [contributorId, unpublish]);
};

/** Returns the merged page context published by all mounted contributors. */
export const useAgentChatPageContext = (): AgentChatPageContext => {
  return useContext(AgentChatContext).pageContext;
};
